/* global React */
/* ──────────────────────────────────────────────────────────────────────
   Services Hub — /services/
   ──────────────────────────────────────────────────────────────────────
   "Stage Explorer" pattern (2026-04-30 redesign #2 — replaces the
   lifecycle path which was rejected as still too timeline-y).

   Layout:
     ▸ Compact navy hero (intro band)
     ▸ Light band split into two columns:
         LEFT (40%): slim vertical selector list of all 11 stages
         RIGHT (60%): featured panel showing the active stage with
                      huge number, title, tagline, services chips, CTA
     ▸ Hover or click any selector item → that stage becomes the
       featured panel content
     ▸ Click "Open detail" in the featured panel → navigates to the
       stage's detail page at /services/<slug>/

   Animation: the panel re-mounts on activeIndex change (via React `key`),
   triggering a CSS fade-up keyframe each transition. Smooth + satisfying.

   Mobile: stacks vertically; selector becomes a horizontal scroll strip.

   CSS namespace: .dz-svc-explorer__* (the old .dz-svc-life__* lifecycle
   path styles are now dead code in home.css — harmless to leave).
   ────────────────────────────────────────────────────────────────────── */

const { useState: useHubState } = React;

/* ── Commercial prose band (B1, 2026-06-28) ───────────────────────────────
   Renders one search-intent block using the SAME anatomy as the existing
   Service Deep Dives band (.dz-services-hub__deep-*). Design-preserving:
   identical wrapper classNames, head structure and card grid — no new CSS.
   `block.cards[].href` is optional: with an href the card is an <a> (as in
   the deep band); without one it is a static <div> carrying crawlable prose. */
function ServicesProseBand({ block }) {
  if (!block) return null;
  return (
    <section className="dz-services-hub__deep-band">
      <div className="dz-services-hub__inner">
        <header className="dz-services-hub__deep-head">
          <span className="dz-eyebrow dz-eyebrow--on-dark">{block.eyebrow}</span>
          <h2
            className="dz-services-hub__deep-title"
            dangerouslySetInnerHTML={{ __html: block.title }}
          />
          <p className="dz-services-hub__deep-lead">{block.lead}</p>
        </header>
        {block.cards && block.cards.length ? (
          <div className="dz-services-hub__deep-grid">
            {block.cards.map((card, i) =>
              card.href ? (
                <a
                  key={i}
                  className={'dz-services-hub__deep-card' + (card.credential ? ' dz-services-hub__deep-card--credential' : '')}
                  href={card.href}
                  {...(/^https?:/i.test(card.href) ? { target: '_blank', rel: 'noopener' } : {})}
                >
                  <span className="dz-services-hub__deep-card-label">{card.label}</span>
                  <span className="dz-services-hub__deep-card-note">{card.note}</span>
                  <span className="dz-services-hub__deep-card-arrow" aria-hidden="true">→</span>
                </a>
              ) : (
                <div
                  key={i}
                  className={'dz-services-hub__deep-card' + (card.credential ? ' dz-services-hub__deep-card--credential' : '')}
                >
                  <span className="dz-services-hub__deep-card-label">{card.label}</span>
                  <span className="dz-services-hub__deep-card-note">{card.note}</span>
                </div>
              )
            )}
          </div>
        ) : null}
        {block.ctaLink && <p className="dz-services-hub__cta-link" dangerouslySetInnerHTML={{ __html: block.ctaLink }} />}
      </div>
    </section>
  );
}

/* ── FAQ accordion — exact home/TwinMaq pattern: lead + toggle reveals the
   list; each question is a <details> accordion. All Q&A text stays in the
   DOM (hidden attr) → crawlable. Answers byte-match the FAQPage schema. ── */
function ServicesFaq({ data }) {
  if (!data) return null;
  return (
    <section className="dz-section dz-faq" aria-label={data.sectionAriaLabel}>
      <div className="dz-section__inner">
        <span className="dz-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-h2">{data.titleLeading} <em>{data.titleAccent}</em></h2>
        <p className="dz-lead">{data.lead}</p>
        <div className="dz-faq__list" id="dz-svc-faq-list">
          {data.items.map((it, i) => (
            <details key={i} className="dz-faq-item">
              <summary>{it.q}</summary>
              <div className="dz-faq-item__body">{it.a}</div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicesHub() {
  const hub = window.DZ_SERVICES.hub;
  const stages = window.DZ_SERVICES.stages;
  const [activeIndex, setActiveIndex] = useHubState(0);

  const active = stages[activeIndex] || stages[0];

  return (
    <main className="dz-services-hub" id="dz-main" aria-label={hub.sectionAriaLabel}>

      {/* ── Compact hero band (navy) ── */}
      <section className="dz-services-hub__intro-band">
        <div className="dz-services-hub__inner">
          <header className="dz-services-hub__head">
            <span className="dz-eyebrow">{hub.eyebrow}</span>
            <h1 className="dz-services-hub__title">
              {hub.titleLeading} <em>{hub.titleAccent}</em>
            </h1>
            <p className="dz-services-hub__subtitle">{hub.titleTrailing}</p>
            <p
              className="dz-services-hub__intro"
              dangerouslySetInnerHTML={{ __html: hub.intro }}
            />
            {hub.proof && <p className="dz-services-hub__proof">{hub.proof}</p>}
            {hub.ctas && (
              <div className="dz-services-hub__cta">
                {hub.ctas.map((c, i) => (
                  <a key={i} className={'dz-btn ' + (c.outline ? 'dz-btn--profile-outline dz-btn--on-dark' : 'dz-btn--aqua')} href={c.href}>{c.label}</a>
                ))}
              </div>
            )}
          </header>
        </div>
      </section>

      {/* ── Stage Explorer band (light) — selector + featured panel ── */}
      <section className="dz-services-hub__explorer-band" id="svc-explorer">
        <div className="dz-services-hub__inner">
          <div className="dz-svc-explorer">

            {/* LEFT — slim selector list */}
            <nav
              className="dz-svc-explorer__selector"
              aria-label={hub.selectorAriaLabel || 'BIM lifecycle stages'}
            >
              <span className="dz-svc-explorer__selector-eyebrow">
                {hub.selectorEyebrow || 'Eleven stages'}
              </span>
              <ol className="dz-svc-explorer__list">
                {stages.map((stage, i) => (
                  <li key={stage.id}>
                    <a
                      href={(window.location.pathname.startsWith('/ar/') ? '/ar/services/' : '/services/') + stage.slug + '/'}
                      className={'dz-svc-explorer__item' + (activeIndex === i ? ' is-active' : '')}
                      style={{ textDecoration: 'none', boxSizing: 'border-box' }}
                      onClick={(e) => { e.preventDefault(); setActiveIndex(i); }}
                      onMouseEnter={() => setActiveIndex(i)}
                      onFocus={() => setActiveIndex(i)}
                      aria-current={activeIndex === i ? 'true' : 'false'}
                    >
                      <span className="dz-svc-explorer__item-num">{stage.number}</span>
                      <span className="dz-svc-explorer__item-label">{stage.label}</span>
                      <span className="dz-svc-explorer__item-marker" aria-hidden="true" />
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            {/* RIGHT — featured panel (re-mounts on activeIndex change for
                a fresh fade-up animation each time) */}
            <article
              key={activeIndex}
              className="dz-svc-explorer__panel"
              aria-live="polite"
            >
              <span className="dz-svc-explorer__big-num" aria-hidden="true">
                {active.number}
              </span>

              <div className="dz-svc-explorer__panel-body">
                <span className="dz-svc-explorer__panel-eyebrow">
                  {(hub.panelEyebrowTpl || 'Stage {n} · BIM Lifecycle').replace('{n}', active.number)}
                </span>
                <h2 className="dz-svc-explorer__panel-title">{active.label}</h2>
                {active.tagline ? (
                  <p className="dz-svc-explorer__panel-tagline">{active.tagline}</p>
                ) : null}

                {active.services && active.services.length ? (
                  <div className="dz-svc-explorer__services-block">
                    <span className="dz-svc-explorer__services-eyebrow">
                      {(hub.servicesEyebrowTpl || '{n} services in this stage').replace('{n}', active.services.length)}
                    </span>
                    <ul className="dz-svc-explorer__services-list" aria-label={hub.servicesListAriaLabel || 'Services in this stage'}>
                      {active.services.map((svc) => (
                        <li key={svc.id} className="dz-svc-explorer__service">
                          <span className="dz-svc-explorer__service-marker" aria-hidden="true" />
                          <div className="dz-svc-explorer__service-body">
                            <h3 className="dz-svc-explorer__service-name">
                              {svc.name}
                            </h3>
                            {svc.summary ? (
                              <p className="dz-svc-explorer__service-summary">{svc.summary}</p>
                            ) : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <a
                  href={(window.location.pathname.startsWith('/ar/') ? '/ar/services/' : '/services/') + active.slug + '/'}
                  className="dz-svc-explorer__cta"
                >
                  <span>{(hub.ctaTpl || 'Explore {label} services').replace('{label}', active.label)}</span>
                  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                    <path
                      d="M3 8h9M8 4l4 4-4 4"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </a>
              </div>
            </article>

          </div>
        </div>
      </section>

      {/* ── DEEP-DIVE LANDINGS band (navy) — 2026-05-11 ──────────────────
          The 11-stage explorer above is organised by lifecycle phase.
          But many clients arrive searching for a single named service
          ("Arch BIM", "MEP coordination", "Clash detection", "Scan to
          BIM", "HBIM") and don't want to figure out which stage that
          phase lives in. This row surfaces dedicated landing pages for
          the 5 most-searched services so they're reachable straight
          from the hub without having to traverse the explorer. */}
      {/* Deep-dive band is fully data-driven via hub.deep so the AR
          mirror can override eyebrow/title/lead/cards and AR URLs.
          English fallback object preserves the original behavior on
          pages that haven't been updated. */}
      {(() => {
        const deep = hub.deep || {
          eyebrow: 'SERVICE DEEP DIVES',
          title:   'Service-specific landing pages — <em>the most-asked BIM offerings.</em>',
          lead:    'Named services that cut across the lifecycle, with dedicated pages for clients evaluating us against specific BIM disciplines.',
          cards: [
            { label: 'Arch BIM (LOD 300 + 400)',     note: 'Federated architectural BIM, tender to fabrication.', href: '/arch-bim/' },
            { label: 'MEP BIM coordination',          note: 'Federated MEP that ships without clashes at site.',   href: '/mep-bim/' },
            { label: 'Clash detection & BIM coord.',  note: 'Every conflict resolved before tender release.',      href: '/clash-detection/' },
            { label: 'Scan to BIM',                   note: 'Measured as-built from point cloud, at ≤5mm.',        href: '/scan-to-bim/' },
            { label: 'Heritage BIM (HBIM)',           note: 'Preservation-led BIM for Saudi cultural icons.',      href: '/hbim/' },
            { label: 'ISO 19650-2 certified',         note: 'The standard your project deserves.',                 href: '/iso-19650/', credential: true }
          ]
        };
        return (
          <section className="dz-services-hub__deep-band">
            <div className="dz-services-hub__inner">
              <header className="dz-services-hub__deep-head">
                <span className="dz-eyebrow dz-eyebrow--on-dark">{deep.eyebrow}</span>
                <h2
                  className="dz-services-hub__deep-title"
                  dangerouslySetInnerHTML={{ __html: deep.title }}
                />
                <p className="dz-services-hub__deep-lead">{deep.lead}</p>
              </header>

              <div className="dz-services-hub__deep-grid">
                {deep.cards.map((card, i) => (
                  <a
                    key={i}
                    className={'dz-services-hub__deep-card' + (card.credential ? ' dz-services-hub__deep-card--credential' : '')}
                    href={card.href}
                  >
                    <span className="dz-services-hub__deep-card-label">{card.label}</span>
                    <span className="dz-services-hub__deep-card-note">{card.note}</span>
                    <span className="dz-services-hub__deep-card-arrow" aria-hidden="true">→</span>
                  </a>
                ))}
              </div>
            </div>
          </section>
        );
      })()}

      {/* ── Commercial content expansion (B1, 2026-06-28) ─────────────────
          Search-intent prose blocks added AROUND the Stage Explorer so the
          lifecycle-delivery substance is crawlable as static text. Each
          reuses the existing deep-band / faq anatomy (design-preserving).
          Stage Explorer interaction above is untouched. ─────────────────── */}
      {hub.commercial ? (
        <React.Fragment>
          <ServicesProseBand block={hub.commercial.model} />
          <ServicesProseBand block={hub.commercial.why} />
          <ServicesProseBand block={hub.commercial.whenStages} />
          <ServicesProseBand block={hub.commercial.standards} />
          <ServicesProseBand block={hub.commercial.engage} />
          <ServicesProseBand block={hub.commercial.workflow} />
          <ServicesProseBand block={hub.commercial.benefits} />
          <ServicesFaq        data={hub.commercial.faq} />
        </React.Fragment>
      ) : null}

    </main>
  );
}

window.ServicesHub = ServicesHub;
